import type { CityConfig } from "../city-template/types";

export const pocatelloConfig: CityConfig = {
  city: "Pocatello",
  state: "Idaho",
  stateAbbr: "ID",
  slug: "pocatello",
  route: "/cities/pocatello",
  title: "Pocatello ID Passport — Expedited & Emergency Service | Fast Passport Center",
  metaDescription: "Expedited and emergency passport services in Pocatello, Idaho — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Pocatello, Chubbuck, Idaho State University, and all of Bannock County.",
  h1: "Expedited Passport Services in Pocatello, ID",
  heroSubtitle: "ISU study abroad program departing for Europe in a few weeks? Business trip to Asia connecting through Salt Lake City? Pocatello residents — from Idaho State University students and faculty to ON Semiconductor professionals to Bannock County families — trust us for fast, reliable passport processing. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Scenic%20view%20of%20Pocatello%20Idaho%20with%20historic%20downtown%20district%20and%20the%20Portneuf%20River%20valley%20surrounded%20by%20mountains%2C%20golden%20hour%20sunlight%20illuminating%20the%20city%2C%20Idaho%20State%20University%20campus%20visible%20in%20the%20distance%2C%20wide%20landscape%20shot%20with%20Bannock%20County%20foothills%20and%20dramatic%20sky%2C%20editorial%20travel%20photography&width=1800&height=700&seq=pocatello-hero-2026&orientation=landscape",
  heroImageAlt: "Pocatello Idaho Portneuf River valley with mountains at golden hour",
  geoPosition: "42.8713;-112.4455",
  geoPlacename: "Pocatello, Idaho",
  lastModified: "2026-06-15",
  regionalAgency: {
    name: "Denver Passport Agency",
    address: "Cherry Creek III, 3151 S Vaughn Way, Suite 600, Aurora, CO 80014",
    distance: "Closest regional agency — approximately 650 miles, appointments required",
  },
  nearbyCitiesZips: [
    { name: "Pocatello", zips: "83201, 83202, 83204, 83205, 83206, 83209" },
    { name: "Chubbuck", zips: "83202" },
    { name: "Idaho Falls", zips: "83401, 83402, 83404, 83406" },
    { name: "Blackfoot", zips: "83221" },
    { name: "American Falls", zips: "83211" },
    { name: "Fort Hall", zips: "83203" },
    { name: "Inkom", zips: "83245" },
  ],
  benefits: [
    { icon: "ri-flashlight-line", title: "24-Hour Southeast Idaho Emergency Processing", desc: "Reserved courier access — as fast as 24 hours for Pocatello and Bannock County travelers with immediate international departures." },
    { icon: "ri-flight-takeoff-line", title: "PIH & IDA Airport Expertise", desc: "Pocatello Regional Airport and Idaho Falls Airport connect to Salt Lake City and Denver hubs for international routes. We know the schedules and connection dynamics." },
    { icon: "ri-graduation-cap-line", title: "ISU Student & Faculty Focus", desc: "Idaho State University students studying abroad, faculty attending international conferences, and researchers presenting globally — we understand academic passport needs." },
    { icon: "ri-home-heart-line", title: "Bannock County Family Specialists", desc: "From first-time passports for Chubbuck kids to renewals for American Falls retirees heading south — we make the process clear, fast, and stress-free." },
  ],
  internalLinks: [
    { name: "Fast passport services in Idaho Falls, ID", route: "/cities/idaho-falls" },
    { name: "Expedited services in Boise, ID", route: "/cities/boise" },
    { name: "Idaho passport services overview", route: "/states/idaho" },
  ],
  fpcOfficeAddress: "3151 S Vaughn Way, Suite 600, Aurora, CO 80014",
  fpcOfficeNote: "Fast Passport Center serves Pocatello via secure registered courier with direct access to State Department processing. Our registered couriers hand-deliver your documents — meaning Pocatello residents get faster results without driving to Denver. Ship from any UPS Store, FedEx Office, or post office in Bannock County.",
  fpcOfficeDistance: "Serving Pocatello, Chubbuck, Blackfoot, American Falls, Fort Hall, Inkom, and all Bannock County communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Pocatello and greater Bannock County. From the ISU campus corridor to the Old Town historic district, from the Chubbuck retail area to the Fort Hall Reservation communities — we serve all of southeast Idaho. Our courier service eliminates the need for any long-distance travel.",
  nearbyCitiesIntro: "We serve Pocatello and all Bannock County communities including Chubbuck, Blackfoot, American Falls, Fort Hall, and Inkom. We also serve travelers departing from Pocatello Regional Airport (PIH) and Idaho Falls Regional Airport (IDA) who need emergency passports.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Pocatello as simple as possible. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee. Most Pocatello travelers start online and ship documents overnight — no office visit required.",
  mistakesIntro: "Southeast Idaho travelers encounter unique passport issues — from ISU study abroad deadlines to last-minute Salt Lake City international connections. Here are the most common passport mistakes we see from this area.",
  testimonialsIntro: "Pocatello and Bannock County travelers — from ISU students to Chubbuck professionals to Blackfoot families — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Pocatello, ID? Here are the most common ones we hear from southeast Idaho travelers.",
  statePageRoute: "/states/idaho",
  statePageName: "Idaho",
  servicesIntro: "Whether you need a renewal before a Salt Lake City international connection, an emergency passport for a last-minute overseas conference, or a first-time passport for your ISU student heading abroad — Fast Passport Center handles it with reserved courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30–100% less than FedEx, Staples, and other third-party passport courier resellers.",
  dmvWebsite: "https://itd.idaho.gov/dmv/",
  dmvLabel: "Idaho Transportation Department — DMV Services",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned", "Daily status updates", "No need to travel to Denver"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned", "Daily status updates"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent", "Updates only by physical mail", "Cannot upgrade speed after submission"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Closest agency is Denver (650 miles)", "Not practical for most Pocatello travelers"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "International flight connecting through SLC in two days? Overseas conference this week? Our emergency courier service submits via reserved agency slots — as fast as 24 hours for Pocatello travelers.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Pocatello residents renew fast — no agency visit required. Perfect for Bannock County professionals and ISU faculty.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide Pocatello residents through every step with clear instructions for the nearest acceptance facility.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require additional forms. We guide you through DS-64 and everything needed to avoid costly mistakes — so your travel plans are not derailed.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear instructions and checklists for Bannock County families.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports so your travel documents match your reservations.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "The passport card is a wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda.",
      bullets: ["Wallet-size convenience", "Land and sea travel", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Pocatello ID", description: "Urgent passport processing for Pocatello Idaho travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Pocatello ID", description: "Fast passport renewal for Pocatello Idaho residents with a previously-issued passport." },
    { name: "First-Time Passport Pocatello ID", description: "New passport application services for Pocatello Idaho residents applying for their first passport." },
    { name: "Child Passport Pocatello ID", description: "Minor passport services for Pocatello Idaho families with children under 16." },
    { name: "Name Change Passport Pocatello ID", description: "Passport name correction after marriage, divorce, or legal name changes for Pocatello residents." },
    { name: "Passport Card Pocatello ID", description: "Passport card services for land and sea crossings from Pocatello Idaho." },
  ],
  testimonials: [
    {
      name: "Dr. Sarah M.",
      location: "Pocatello — ISU Faculty",
      rating: 5,
      text: "My international conference abstract got accepted with only 3 weeks notice and my passport was expired. Fast Passport Center expedited the renewal in 4 business days. Made my presentation in Tokyo.",
      service: "Passport Renewal",
    },
    {
      name: "The Williams Family",
      location: "Chubbuck",
      rating: 5,
      text: "Booked a trip to Europe and realized our teenager's passport expired the week before departure. Fast Passport Center handled it in 6 days and saved our family vacation to London and Paris.",
      service: "Child Passport Renewal",
    },
    {
      name: "Jordan K.",
      location: "ISU Student — Pocatello",
      rating: 5,
      text: "Accepted to a semester abroad in Barcelona with 5 weeks before the program started. Never had a passport. They guided me through everything as a first-time applicant and I got it in 7 days.",
      service: "New Passport",
    },
    {
      name: "Linda R.",
      location: "Blackfoot",
      rating: 5,
      text: "Needed a passport card for regular trips across the border into Canada for family visits. They processed it quickly and the document review caught a small error I would have missed.",
      service: "Passport Card",
    },
  ],
  facilities: [
    {
      id: "pocatello-1",
      name: "Pocatello Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "730 E Clark St",
      city: "Pocatello",
      state: "ID",
      zip: "83201",
      phone: "(208) 232-2477",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Downtown Pocatello",
      website: "https://tools.usps.com/find-location.htm?location=1377568",
      websiteLabel: "USPS Location",
      notes: "Primary passport acceptance facility in Pocatello. Passport photos available. Appointment recommended — call ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "pocatello-2",
      name: "Bannock County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "624 E Center St",
      city: "Pocatello",
      state: "ID",
      zip: "83201",
      phone: "(208) 236-7340",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      distance: "Downtown Pocatello",
      website: "https://www.bannockcounty.us/clerk/",
      websiteLabel: "Bannock County Clerk",
      notes: "County clerk passport acceptance services in downtown Pocatello. Serves all Bannock County residents. No photo services on-site — bring your own photos.",
      icon: "ri-government-line",
    },
    {
      id: "pocatello-3",
      name: "Idaho Falls Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "600 W Sunnyside Rd",
      city: "Idaho Falls",
      state: "ID",
      zip: "83402",
      phone: "(208) 522-1372",
      hours: "Mon–Fri 9:00 AM – 4:00 PM (appointment required)",
      distance: "Idaho Falls — 50 miles north",
      website: "https://tools.usps.com/find-location.htm?location=1377536",
      websiteLabel: "USPS Location",
      notes: "Larger acceptance facility in Idaho Falls serving eastern Idaho. Passport photos available. Good backup option if Pocatello appointments are full.",
      icon: "ri-mail-line",
    },
    {
      id: "pocatello-4",
      name: "Blackfoot Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "165 W Pacific St",
      city: "Blackfoot",
      state: "ID",
      zip: "83221",
      phone: "(208) 785-0710",
      hours: "Mon–Fri 9:00 AM – 4:00 PM",
      distance: "Blackfoot — 25 miles north",
      website: "https://tools.usps.com/find-location.htm?location=1377490",
      websiteLabel: "USPS Location",
      notes: "Smaller acceptance facility serving Blackfoot and northern Bannock County. Limited appointment slots — book well in advance.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — Denver agency is 650 miles away", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — drive 650 miles to Denver or fly there", fpc: "Most Pocatello travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel with proof of departure", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Southeast Idaho Guidance", agency: "Generic — no PIH/IDA airport or ISU expertise", fpc: "Full ISU/regional airport/southeast Idaho knowledge built in" },
  ],
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "ISU Study Abroad Timeline Compression",
      description: "Idaho State University students accepted to study abroad programs often receive acceptance letters with 4–6 weeks until departure. First-time applicants need both an acceptance facility visit and processing time — leaving things to the last two weeks creates panic.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Assuming SLC Connection Doesn't Need a Passport",
      description: "Pocatello travelers frequently book domestic flights to Salt Lake City connecting to international destinations without checking passport validity. The passport must be valid at PIH or IDA check-in, not just at the SLC international gate.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Violations",
      description: "Many southeast Idaho pharmacies and big-box stores produce photos that fail State Department standards. We catch this in review before it causes a rejection and adds weeks to your timeline.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Missing Parental Consent for Minors",
      description: "Bannock County families frequently overlook the DS-3053 consent form when one parent is unavailable. This is an automatic rejection — we guide you through it correctly.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Not Accounting for 6-Month Rule",
      description: "Pocatello travelers booking to Asia, Europe, or South America often do not know their passport must have 6 months remaining beyond the return date. We flag this immediately during review.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes from your home, office, or dorm." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance tailored to your travel situation." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Pocatello clients ship from any UPS Store or FedEx Office in Bannock County." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you do not have to drive 650 miles to Denver." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up — just certainty." },
  ],
  faqs: [
    { q: "How fast can I get a passport in Pocatello?", a: "We offer Pocatello-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. We review your documents before submission to catch errors that cause delays." },
    { q: "Is there a passport office in Pocatello?", a: "The Pocatello Main Post Office at 730 E Clark St serves as a passport acceptance facility for first-time applications. The Bannock County Clerk at 624 E Center St also provides acceptance services. For expedited processing beyond the standard timeline, our registered courier service is the fastest option." },
    { q: "Can ISU students apply for passports on campus?", a: "Idaho State University does not have an on-campus passport acceptance facility. ISU students should use the Pocatello Post Office or Bannock County Clerk for their acceptance clerk visit. We provide step-by-step guidance for ISU students applying for study abroad programs." },
    { q: "Where is the closest passport agency to Pocatello?", a: "The Denver Passport Agency is approximately 650 miles away. It requires appointments and serves life-or-death emergencies only. For all other expedited passport needs, our courier channel is faster and eliminates the need for long-distance travel." },
    { q: "Can you help with passports for flights from Pocatello Airport?", a: "Yes. Pocatello Regional Airport (PIH) connects to Salt Lake City International Airport, a major international gateway. Idaho Falls Airport (IDA) is another option 50 miles north. We regularly assist southeast Idaho travelers with expedited passports for connections through both airports." },
    { q: "What documents do Pocatello applicants need?", a: "New passport: certified birth certificate, government-issued photo ID, DS-11 form, and passport photo. Renewal: DS-82 form, current passport, and passport photo. Child passport: DS-11, birth certificate, both parents or notarized consent, and photos. We provide a Pocatello-specific checklist." },
    { q: "Where can I get passport photos in Pocatello?", a: "The Pocatello Post Office, Walgreens on Yellowstone Ave, and Staples on Pole Line Rd all offer passport photo services. We recommend confirming photo standards compliance before visiting." },
    { q: "What is the best price for expedited service in Pocatello?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers." },
    { q: "How do I check my Pocatello passport status?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov. Processing from Pocatello to final return shipping typically matches your chosen speed tier." },
  ],
  travelTiming: {
    title: "Travel & Passport Timing for Pocatello Residents",
    intro: "Pocatello's position as southeast Idaho's education and medical hub — home to Idaho State University and the Portneuf Medical Center — creates unique passport timing challenges. Here's what Bannock County travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "PIH — Pocatello Regional Airport",
        subtitle: "Regional airport with daily Delta Connection flights to Salt Lake City",
        description: "Pocatello Regional Airport offers daily flights to Salt Lake City International Airport (SLC), a major Delta Air Lines hub with nonstop service to London, Paris, Amsterdam, Mexico City, and Tokyo. While PIH is a small regional airport, it connects southeast Idaho to virtually every international destination through SLC. The convenience of a 15-minute drive to PIH sometimes masks the need for passport readiness.",
        tip: "PIH-to-SLC connection flights mean your passport must be valid at check-in in Pocatello, not just at the SLC international gate. Delta agents at PIH will check your international documentation.",
      },
      {
        icon: "ri-graduation-cap-line",
        title: "ISU — Idaho State University International Programs",
        subtitle: "Semester abroad, faculty research, international conferences",
        description: "ISU sends students and faculty to dozens of countries each year through study abroad programs, research collaborations, and academic conferences. The university's health sciences programs — pharmacy, nursing, physician assistant studies — send students to clinical rotations and medical missions abroad. Faculty in engineering, geosciences, and biological sciences regularly travel internationally for conferences and field research. The acceptance-to-departure timeline is often compressed to 6–8 weeks.",
        tip: "ISU study abroad and conference travel deadlines are fixed. Start your passport process the moment you receive program acceptance or conference registration confirmation.",
      },
      {
        icon: "ri-train-line",
        title: "Southeast Idaho Regional Access",
        subtitle: "Serving a large geographic area from a central hub",
        description: "Pocatello serves as the commercial and medical hub for a wide geographic region including American Falls, Blackfoot, Fort Hall, and the Shoshone-Bannock Tribes communities. Residents from surrounding rural areas often come to Pocatello for passport acceptance services, DMV visits, and photo services before traveling internationally. The wide catchment area means acceptance facility appointments fill quickly during peak seasons.",
        tip: "Book your Pocatello acceptance facility appointment 2–3 weeks in advance during summer and winter holiday seasons. The Bannock County Clerk often has better availability than the post office.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "Pocatello & Regional Healthcare Travel",
      description: "Portneuf Medical Center and ISU health sciences programs generate regular international travel — medical missions to Central America, pharmacy rotations in Europe, and nursing exchanges in Southeast Asia. These travelers often face strict credentialing and departure deadlines that leave no room for passport delays.",
    },
  },
  destinations: {
    title: "Where Pocatello Travelers Go — And Why Passports Get Urgent",
    intro: "Southeast Idaho travelers go abroad for education, healthcare, business, and family reasons. The ISU connection drives more international travel than most cities of similar size.",
    stats: [
      { stat: "35%", label: "of Pocatello expedited requests are education-related — ISU students, faculty, researchers" },
      { stat: "4–6 Weeks", label: "average lead time from ISU program acceptance to international departure date" },
      { stat: "Apr–Aug", label: "peak season when summer study abroad, research trips, and family vacations overlap" },
      { stat: "6 Days", label: "typical turnaround for Pocatello-area expedited passport processing" },
    ],
    destinations: [
      { name: "Europe", context: "ISU semester programs in London, Paris, Madrid; faculty conferences in Germany and Italy", urgency: "Program acceptance with 4–6 weeks until orientation and departure", icon: "ri-map-pin-2-line" },
      { name: "Mexico & Central America", context: "ISU medical missions, pharmacy clinical rotations, dental outreach programs", urgency: "Mission trips with fixed departure dates and credentialing deadlines", icon: "ri-map-pin-2-line" },
      { name: "Asia", context: "ISU engineering and business exchange programs in Japan, South Korea, China", urgency: "Visa processing timelines compound with already-tight passport windows", icon: "ri-map-pin-2-line" },
      { name: "Canada", context: "Family visits to British Columbia and Alberta, Banff and Jasper national parks", urgency: "Road trips booked 2–4 weeks out during summer months", icon: "ri-map-pin-2-line" },
      { name: "Caribbean Cruises", context: "Popular with Bannock County families, departing Seattle or Florida ports", urgency: "Cruise line documentation deadlines with no flexibility", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other Southeast Idaho Service Areas",
    crossLinksDesc: "We serve travelers across eastern Idaho with the same fast, reliable passport processing.",
    crossLinks: [
      { name: "Idaho Falls", route: "/cities/idaho-falls" },
      { name: "Boise", route: "/cities/boise" },
    ],
  },
  photoLocations: [
    { name: "Pocatello Post Office", address: "730 E Clark St, Pocatello, ID 83201", hours: "Mon–Fri 9 AM – 4 PM" },
    { name: "Walgreens — Pocatello", address: "905 Yellowstone Ave, Pocatello, ID 83201", hours: "Daily 7 AM – 11 PM" },
    { name: "Staples — Pocatello", address: "4240 Pole Line Rd, Pocatello, ID 83201", hours: "Mon–Fri 8 AM – 8 PM, Sat 9 AM – 6 PM" },
    { name: "The UPS Store — Pocatello", address: "1599 Yellowstone Ave, Pocatello, ID 83201", hours: "Call for hours" },
  ],
  dmvLocations: [
    {
      name: "Bannock County DMV — Pocatello",
      address: "130 N 6th Ave, Pocatello, ID 83201",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Driver licenses, REAL ID, vehicle registration. Appointment recommended.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "PIH",
      name: "Pocatello Regional Airport",
      distance: "About 10 minutes from downtown Pocatello",
      address: "1950 Airport Way, Pocatello, ID 83204",
      note: "Regional airport with daily Delta Connection flights to Salt Lake City International Airport (SLC), a major international gateway.",
      flightUrl: "https://www.pocatello.gov/252/Airport",
      flightLabel: "PIH Airport Website",
      featured: true,
    },
    {
      code: "IDA",
      name: "Idaho Falls Regional Airport",
      distance: "About 50 minutes from Pocatello",
      address: "2140 N Skyline Dr, Idaho Falls, ID 83402",
      note: "Larger regional airport with flights to SLC, Denver, Seattle, Phoenix, and Minneapolis — all major international gateway hubs.",
      flightUrl: "https://www.idahofallsidaho.gov/803/Airport",
      flightLabel: "IDA Airport Website",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from Idaho Vital Records. Online, mail, or in-person options available for Bannock County.",
      url: "https://healthandwelfare.idaho.gov/services-programs/birth-death-marriage-divorce",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through Idaho Vital Records. Required for estate and legal matters in Bannock County.",
      url: "https://healthandwelfare.idaho.gov/services-programs/birth-death-marriage-divorce",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the Bannock County Clerk. Certified copies available for name changes and legal purposes.",
      url: "https://www.bannockcounty.us/clerk/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Bannock County District Court. Required for name change passport corrections.",
      url: "https://www.bannockcounty.us/courts/",
    },
  ],
};